/**
 * **交易记录对话框**（批 `127` · `DR-073`）—— 撤销买卖的入口
 *
 * ## 🔴 为什么撤销要做成一个"记录列表"而不是持仓行上的一个按钮
 *
 * 撤销的对象是**某一笔**，而持仓行只知道"现在持有多少"。不把记录列出来，用户无法确认
 * 自己撤的是哪一笔（尤其同一天卖了两次时）。列表 + 每行一个「撤销」是唯一能自证的选择。
 *
 * ## 🔴 只有最新一笔可撤销（栈式）
 *
 * 撤销中间那笔会让成本价变化，而后面卖出的 `realized_pnl` 已经按旧成本落库 ——
 * 两者从此对不上，且无法在不重写历史的前提下修复。所以服务端只允许从后往前撤，
 * 端侧照它给的 `revocable` 显示，**不自己判断**（否则就是第二份口径）。
 *
 * ## ⚠️ 为什么能静态渲染
 *
 * 用普通 `Dialog`（MUI Portal）。本对话框**没有**配静态渲染断言（Portal 在
 * `renderToStaticMarkup` 下内容不进产物，见铁律 100/121）—— 它的行为由
 * `features/investment.spec.ts` 的纯逻辑 + 集成测试的 ⑬ 组共同兜住。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  e4ToText,
  e4ToTrimmedText,
  formatMoneyText,
  toCents,
  type HoldingView,
  type InvestmentTradeRowView,
} from '@jiaying/shared';
import { listInvestmentTrades, revokeInvestmentTrade } from '../api/endpoints.js';
import { ApiError } from '../api/http.js';
import { invalidateAfterTxnWrite } from '../features/invalidate.js';
import { actionsOf, holdingTitleOf, isCleared, qtyTextOf } from '../features/investment.js';
import {
  BTN_CANCEL,
  BTN_REVOKE,
  DLG_TRADES_TITLE,
  EMPTY_TRADES,
  HINT_REVOKE_WHY,
  HINT_TRADES,
} from '../features/investment-labels.js';

export interface InvestmentTradesDialogProps {
  holding: HoldingView;
  onClose: () => void;
}

/** 一条流水的动作文案（品种相关：基金是"申购/赎回"，存款是"存入/取出"） */
function actionText(kind: string, action: string): string {
  const acts = actionsOf(kind);
  if (action === 'buy') return acts.buy;
  if (action === 'sell') return acts.sell;
  if (action === 'dividend') return '分红';
  if (action === 'settle') return '结息';
  return action;
}

export function InvestmentTradesDialog({ holding, onClose }: InvestmentTradesDialogProps): ReactElement {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const currency = holding.currency;

  const list = useQuery({
    queryKey: ['investment-trades', holding.id],
    queryFn: () => listInvestmentTrades(holding.id),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeInvestmentTrade(id),
    onSuccess: () => {
      // 撤销会回退账户余额 ⇒ 与记账同一批失效（否则页面数字对不上）
      invalidateAfterTxnWrite(qc);
      void qc.invalidateQueries({ queryKey: ['investment-overview'] });
      void qc.invalidateQueries({ queryKey: ['investment-trades', holding.id] });
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const items: readonly InvestmentTradeRowView[] = list.data?.items ?? [];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {DLG_TRADES_TITLE} · {holdingTitleOf(holding)}
        {isCleared(holding) ? '（已清仓）' : `（${qtyTextOf(holding)} 份）`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {HINT_TRADES}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {HINT_REVOKE_WHY}
          </Typography>

          {list.isPending && <Typography variant="body2">读取中…</Typography>}
          {list.isError && <Alert severity="error">交易记录读取失败：{String(list.error)}</Alert>}
          {!list.isPending && !list.isError && items.length === 0 && (
            <Typography variant="body2" color="text.secondary" data-testid="trades-empty">
              {EMPTY_TRADES}
            </Typography>
          )}

          {items.map((t) => (
            <Box
              key={t.id}
              data-testid={`trade-row-${t.id}`}
              sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ minWidth: 88 }}>
                  {t.date}
                </Typography>
                <Typography variant="body2" sx={{ minWidth: 56 }}>
                  {actionText(holding.kind, t.action)}
                </Typography>
                {t.quantityE4 !== null && (
                  <Typography variant="body2" color="text.secondary">
                    {e4ToTrimmedText(t.quantityE4)} 份 · {t.priceE4 === null ? '—' : e4ToText(t.priceE4)}
                  </Typography>
                )}
                {/* 🔴 历史行没有成交金额 ⇒ 不拿 ¥0.00 冒充 */}
                {t.grossCents !== null && (
                  <Typography variant="body2" color="text.secondary">
                    {formatMoneyText(toCents(t.grossCents), currency)}
                  </Typography>
                )}
                {t.realizedPnlCents !== null && t.action === 'sell' && (
                  <Typography
                    variant="body2"
                    sx={(theme) => ({
                      color:
                        (t.realizedPnlCents ?? 0) >= 0
                          ? theme.jiaying.tokens['c-rise']
                          : theme.jiaying.tokens['c-fall'],
                    })}
                  >
                    {formatMoneyText(toCents(t.realizedPnlCents), currency, { signed: true })}
                  </Typography>
                )}
                {t.revocable && (
                  <Button
                    size="small"
                    color="warning"
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(t.id)}
                    data-testid={`trade-revoke-${t.id}`}
                  >
                    {revoke.isPending && revoke.variables === t.id ? '撤销中…' : BTN_REVOKE}
                  </Button>
                )}
              </Stack>
            </Box>
          ))}

          {error !== null && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={revoke.isPending}>
          {BTN_CANCEL}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
